import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import { LinkIcon, CheckCircle2, AlertCircle, XCircle, HelpCircle } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, LinkButton, SecondaryButton, Table, StatusBadge, Field, Select, SortableHeader, useTableSort } from "@/components/admin/AdminUI";
import { ClassificationBadge } from "@/components/public/ClassificationBadge";
import { ExportCsvButton } from "@/components/admin/ExportCsvButton";
import { ImportClothingFromUrl } from "@/components/admin/ImportClothingFromUrl";
import { effectiveClassification, formatDate } from "@/lib/format";
const LINK_LABEL = {
  ok: { label: "OK", className: "bg-leaf/10 text-leaf border-leaf/20", icon: CheckCircle2 },
  not_found: { label: "404 Not found", className: "bg-clay/10 text-clay border-clay/30", icon: XCircle },
  gone: { label: "410 Gone", className: "bg-clay/10 text-clay border-clay/30", icon: XCircle },
  client_error: { label: "Client error", className: "bg-amber/10 text-amber border-amber/30", icon: AlertCircle },
  server_error: { label: "Server error", className: "bg-amber/10 text-amber border-amber/30", icon: AlertCircle },
  unreachable: { label: "Unreachable", className: "bg-amber/10 text-amber border-amber/30", icon: AlertCircle },
};

function LinkBadge({ status, checkedAt }) {
  if (!status) return <span className="text-xs text-[#6C757D]">—</span>;
  const cfg = LINK_LABEL[status] || { label: status, className: "bg-gray-100 text-gray-700", icon: HelpCircle };
  const Icon = cfg.icon;
  return (
    <div className="flex flex-col items-start gap-1" data-testid={`link-badge-${status}`}>
      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${cfg.className}`}>
        <Icon className="w-3 h-3" /> {cfg.label}
      </span>
      {checkedAt && (
        <span className="text-[10px] text-[#6C757D]">{formatDate(checkedAt)}</span>
      )}
    </div>
  );
}

// RED beats YELLOW beats GREEN beats null when sorting "worst first".
const CLASSIFICATION_RANK = { RED: 3, YELLOW: 2, GREEN: 1 };

export default function AdminClothingList() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const brand_id = params.get("brand_id") || "";
  const link_status = params.get("link_status") || "";
  const classification = params.get("classification") || "";
  const manually_reviewed = params.get("manually_reviewed") || "";
  const q = params.get("q") || "";
  const [importOpen, setImportOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const { sort, toggleSort, sortRows } = useTableSort();

  const { data: rawItems = [] } = useQuery({
    queryKey: ["admin-clothing", { status, brand_id, q, link_status, manually_reviewed }],
    queryFn: async () => (await api.get("/admin/clothing-items", { params: { status: status || undefined, brand_id: brand_id || undefined, q: q || undefined, link_status: link_status || undefined, manually_reviewed: manually_reviewed || undefined } })).data,
  });
  const { data: brands = [] } = useQuery({ queryKey: ["admin-brands"], queryFn: async () => (await api.get("/admin/brands")).data });

  // Decorate each item with the derived overall classification (used by both
  // the new column AND the new client-side filter / sort).
  const items = useMemo(() => {
    const decorated = rawItems.map((i) => ({
      ...i,
      _classification: effectiveClassification(i.materials),
    }));
    const filtered = classification
      ? decorated.filter((i) => i._classification === classification)
      : decorated;
    return sortRows(filtered, sortValue);
  }, [rawItems, classification, sort, sortRows]);

  function update(k, v) {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  }

  async function checkLinks() {
    if (!window.confirm(`Check the link health of all ${items.length > 0 ? "" : ""}clothing items? This sends one HEAD request per product URL.`)) return;
    setChecking(true);
    const t = toast.loading("Checking link health…");
    try {
      const { data } = await api.post("/admin/clothing-items/check-link-health");
      const s = data.summary || {};
      const broken = (s.not_found || 0) + (s.gone || 0);
      const issues = (s.client_error || 0) + (s.server_error || 0) + (s.unreachable || 0);
      const ok = s.ok || 0;
      toast.success(`Checked ${data.checked}: ${ok} OK · ${broken} broken · ${issues} issues`, { id: t });
      qc.invalidateQueries({ queryKey: ["admin-clothing"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link check failed", { id: t });
    } finally {
      setChecking(false);
    }
  }

  return (
    <div data-testid="admin-clothing-list">
      <PageHeader
        title="Clothing items"
        subtitle={`${items.length} items`}
        actions={
          <>
            <SecondaryButton
              onClick={checkLinks}
              disabled={checking}
              data-testid="check-link-health-button"
            >
              <LinkIcon className="w-4 h-4 inline mr-1" />
              {checking ? "Checking…" : "Check links"}
            </SecondaryButton>
            <SecondaryButton onClick={() => setImportOpen(true)} data-testid="open-import-from-url">Import from URL</SecondaryButton>
            <ExportCsvButton endpoint="/admin/clothing-items/export.csv" filename="clothing-items.csv" testid="export-clothing-csv" />
            <LinkButton to="/admin/clothing-items/new">+ New item</LinkButton>
          </>
        }
      />
      <ImportClothingFromUrl open={importOpen} onOpenChange={setImportOpen} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        <Field label="Search"><input className="w-full h-10 px-3 border border-[#E9ECEF] rounded-md text-sm" defaultValue={q} onBlur={(e) => update("q", e.target.value)} placeholder="Name or brand…" data-testid="admin-clothing-search" /></Field>
        <Field label="Status">
          <Select value={status} onChange={(e) => update("status", e.target.value)}>
            <option value="">All</option>
            <option value="pending_review">Pending review</option>
            <option value="approved_featured">Approved (visible on website)</option>
            <option value="not_sustainable">Not sustainable</option>
            <option value="needs_more_information">Needs more info</option>
            <option value="archived">Archived</option>
          </Select>
        </Field>
        <Field label="Brand">
          <Select value={brand_id} onChange={(e) => update("brand_id", e.target.value)}>
            <option value="">All brands</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        <Field label="Classification">
          <Select
            value={classification}
            onChange={(e) => update("classification", e.target.value)}
            data-testid="classification-filter"
          >
            <option value="">All</option>
            <option value="GREEN">GREEN</option>
            <option value="YELLOW">YELLOW</option>
            <option value="RED">RED</option>
          </Select>
        </Field>
        <Field label="Link health">
          <Select
            value={link_status}
            onChange={(e) => update("link_status", e.target.value)}
            data-testid="link-health-filter"
          >
            <option value="">All</option>
            <option value="ok">OK</option>
            <option value="not_found">404 Not found</option>
            <option value="gone">410 Gone</option>
            <option value="client_error">Client error</option>
            <option value="server_error">Server error</option>
            <option value="unreachable">Unreachable</option>
            <option value="unchecked">Not yet checked</option>
          </Select>
        </Field>
        <Field label="Manually reviewed">
          <Select
            value={manually_reviewed}
            onChange={(e) => update("manually_reviewed", e.target.value)}
            data-testid="manually-reviewed-filter"
          >
            <option value="">All</option>
            <option value="yes">Yes — already checked</option>
            <option value="no">No — still to check</option>
          </Select>
        </Field>
      </div>

      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-hidden">
        <Table>
          <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-xs uppercase tracking-wider text-[#6C757D]">
            <tr>
              <th className="px-3 py-2.5 font-medium" />
              <SortableHeader label="Item" sortKey="name" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Brand" sortKey="brand_name" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Classification" sortKey="classification" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Status" sortKey="sustainability_status" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Public" sortKey="public_visibility" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Last crawl" sortKey="last_crawled_at" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Link health" sortKey="link_status" sort={sort} onSort={toggleSort} />
              <th className="px-3 py-2.5 font-medium text-center" title="Manually reviewed — I personally checked this item on the brand site">Reviewed</th>
              <th className="px-3 py-2.5 font-medium" />
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b border-[#E9ECEF]">
                <td className="px-3 py-2">{i.image_url ? <img src={i.image_url} alt="" className="w-12 h-12 object-cover rounded" /> : <div className="w-12 h-12 bg-gray-100 rounded" />}</td>
                <td className="px-3 py-2">
                  {/* Fixed-width wrapper so `truncate` actually engages inside a
                      table-auto layout. Using width (not max-width) prevents the
                      cell from expanding past 220px and squeezing siblings. */}
                  <div className="w-[220px]">
                    <div className="font-medium truncate" title={i.name}>{i.name}</div>
                    <div className="text-xs text-[#6C757D] truncate" title={i.product_url}>{i.product_url}</div>
                  </div>
                </td>
                <td className="px-3 py-2">{i.brand_name}</td>
                <td className="px-3 py-2">
                  {i._classification
                    ? <ClassificationBadge classification={i._classification} className="text-[10px]" data-testid={`item-classification-${i.id}`} />
                    : <span className="text-xs text-[#6C757D]">—</span>}
                </td>
                <td className="px-3 py-2"><StatusBadge status={i.sustainability_status} /></td>
                <td className="px-3 py-2">{i.public_visibility ? "Yes" : "No"}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`last-crawl-${i.id}`}>{i.last_crawled_at ? formatDate(i.last_crawled_at) : <span className="text-[#6C757D]">—</span>}</td>
                <td className="px-3 py-2"><LinkBadge status={i.link_status} checkedAt={i.link_checked_at} /></td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={!!i.manually_reviewed}
                    readOnly
                    disabled
                    className="accent-forest cursor-not-allowed opacity-90"
                    aria-label={`Manually reviewed: ${i.manually_reviewed ? "yes" : "no"}`}
                    title="Read-only on the list view. Toggle this from the item's Edit form."
                    data-testid={`manually-reviewed-toggle-${i.id}`}
                  />
                </td>
                <td className="px-3 py-2"><Link to={`/admin/clothing-items/${i.id}`} className="underline text-sm">Edit</Link></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={10} className="px-3 py-12 text-center text-[#6C757D]">No items.</td></tr>}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

// Map a sort-key to a comparable value pulled from the decorated row.
// Booleans become 0/1 so they group cleanly; classification uses its severity rank.
function sortValue(item, key) {
  if (key === "classification") return CLASSIFICATION_RANK[item._classification] ?? 0;
  if (key === "public_visibility") return item.public_visibility ? 1 : 0;
  if (key === "last_crawled_at") return item.last_crawled_at ? new Date(item.last_crawled_at).getTime() : null;
  return item[key] ?? null;
}
